import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Icon,
  useToast,
  Textarea,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Flex,
} from "@chakra-ui/react";
import { FaSignature, FaTrash, FaCheck, FaArrowLeft } from "react-icons/fa";
import { CheckCircle, AlertTriangle, ArrowLeft } from "lucide-react";
import axios from "axios";
import { BASE_URL, CLOUDINARY_CLOUD_NAME } from "../../config/RequestMethod";

interface ESignCanvasProps {
  back: (value: boolean) => void;
  proposalId: string | null;
  onSuccess: () => void;
}

const ESignCanvas: React.FC<ESignCanvasProps> = ({
  back,
  proposalId,
  onSuccess,
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const cancelRef = useRef(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [remark, setRemark] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();

  const clear = () => {
    sigCanvas.current?.clear();
  };

  const openConfirmModal = () => {
    if (sigCanvas.current?.isEmpty()) {
      toast({
        title: "Signature required",
        description: "Please draw your signature before proceeding",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    if (!remark.trim()) {
      toast({
        title: "Remark required",
        description: "Please add a remark or note",
        status: "warning",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }
    setIsDialogOpen(true);
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    setIsSubmitting(true);

    try {
      const dataUrl = sigCanvas?.current?.toDataURL("image/png");
      const blob = await (await fetch(dataUrl || "")).blob();

      const formData = new FormData();
      formData.append("file", blob);
      formData.append("upload_preset", "CRMMatrix");

      // Upload to Cloudinary
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        formData
      );

      const payload = {
        esign: res?.data?.secure_url,
        clientNote: remark,
      };

      // Update proposal
      const res2 = await axios.put(
        `${BASE_URL}client-proposal/${proposalId}`,
        payload
      );

      toast({
        title: "Success!",
        description: res2?.data?.message || "Proposal accepted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });

      // Call success callback to show thank you page
      setTimeout(() => {
        onSuccess();
      }, 1000);
    } catch (error: any) {
      console.error("Error:", error);
      toast({
        title: "Error",
        description:
          error?.response?.data?.message || "Failed to submit signature",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Box maxW="900px" w="full" mx="auto" py={8}>
        {/* Modern Header */}
        <Box
          bg="white"
          borderRadius="2xl"
          boxShadow="xl"
          border="2px solid"
          borderColor="blue.100"
          p={8}
          mb={6}
          position="relative"
          overflow="hidden"
        >
          {/* Background Gradient Accent */}
          <Box
            position="absolute"
            top="-100px"
            right="-100px"
            w="300px"
            h="300px"
            borderRadius="full"
            bgGradient="linear(to-br, blue.100, purple.100)"
            opacity={0.3}
            filter="blur(60px)"
          />

          <VStack spacing={6} position="relative" zIndex={1}>
            {/* Icon & Title */}
            <Flex align="center" gap={4}>
              <Box
                bgGradient="linear(to-br, blue.500, blue.600)"
                p={4}
                borderRadius="2xl"
                boxShadow="lg"
              >
                <Icon as={FaSignature} w={8} h={8} color="white" />
              </Box>
              <VStack align="start" spacing={0}>
                <Text fontSize="2xl" fontWeight="900" color="gray.800">
                  Digital Signature
                </Text>
                <Text fontSize="sm" color="gray.500" fontWeight="500">
                  Sign below to accept this proposal
                </Text>
              </VStack>
            </Flex>

            {/* Signature Canvas */}
            <Box w="full">
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.700"
                mb={3}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Box w="4px" h="4px" borderRadius="full" bg="blue.500" />
                Draw Your Signature
              </Text>
              <Box
                border="3px dashed"
                borderColor="blue.300"
                borderRadius="xl"
                bg="blue.50"
                overflow="hidden"
                position="relative"
                boxShadow="inner"
              >
                {/* Placeholder text */}
                {sigCanvas.current?.isEmpty() && (
                  <Text
                    position="absolute"
                    top="50%"
                    left="50%"
                    transform="translate(-50%, -50%)"
                    color="blue.400"
                    fontSize="lg"
                    fontWeight="600"
                    opacity={0.4}
                    pointerEvents="none"
                    zIndex={0}
                  >
                    Sign here...
                  </Text>
                )}
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#1E40AF"
                  canvasProps={{
                    width: 800,
                    height: 250,
                    className: "sigCanvas",
                    style: { width: "100%", height: "250px" },
                  }}
                />
              </Box>
              <Flex justify="space-between" align="center" mt={3}>
                <Text fontSize="xs" color="gray.500">
                  Use your mouse or finger to sign
                </Text>
                <Button
                  leftIcon={<FaTrash />}
                  onClick={clear}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  fontSize="xs"
                >
                  Clear
                </Button>
              </Flex>
            </Box>

            {/* Remark Input */}
            <Box w="full">
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.700"
                mb={3}
                display="flex"
                alignItems="center"
                gap={2}
              >
                <Box w="4px" h="4px" borderRadius="full" bg="purple.500" />
                Add Your Remarks
              </Text>
              <Textarea
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                placeholder="Type any message, feedback, or note here..."
                size="lg"
                borderRadius="xl"
                bg="white"
                border="2px solid"
                borderColor="gray.200"
                _hover={{ borderColor: "purple.300" }}
                _focus={{
                  borderColor: "purple.500",
                  boxShadow: "0 0 0 1px #9333EA",
                  bg: "white",
                }}
                p={4}
                minH="120px"
                resize="vertical"
                transition="all 0.2s"
                fontSize="sm"
              />
              <Text fontSize="xs" color="gray.500" mt={2}>
                {remark.length} characters
              </Text>
            </Box>

            {/* Action Buttons */}
            <Flex w="full" gap={4} pt={4}>
              <Button
                leftIcon={<ArrowLeft size={16} />}
                onClick={() => back(false)}
                size="lg"
                flex={1}
                fontSize="sm"
                fontWeight="700"
                variant="outline"
                borderWidth="2px"
                borderColor="gray.300"
                color="gray.700"
                _hover={{
                  borderColor: "gray.400",
                  bg: "gray.50",
                }}
                isDisabled={isSubmitting}
              >
                Go Back
              </Button>
              <Button
                leftIcon={<CheckCircle size={16} />}
                onClick={openConfirmModal}
                size="lg"
                flex={1}
                fontSize="sm"
                fontWeight="700"
                bgGradient="linear(to-r, green.400, green.600)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, green.500, green.700)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 20px -4px rgba(34, 197, 94, 0.5)",
                }}
                _active={{
                  transform: "translateY(0)",
                }}
                transition="all 0.2s"
                isLoading={isSubmitting}
                loadingText="Submitting..."
              >
                Accept & Submit
              </Button>
            </Flex>
          </VStack>
        </Box>

        {/* Info Box */}
        <Box
          bg="blue.50"
          border="1px solid"
          borderColor="blue.200"
          borderRadius="xl"
          p={4}
        >
          <HStack spacing={3} align="start">
            <Icon as={AlertTriangle} w={5} h={5} color="blue.600" mt={0.5} />
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="700" color="blue.900">
                Important Notice
              </Text>
              <Text fontSize="xs" color="blue.800" lineHeight="1.6">
                By signing this document, you acknowledge that you have read,
                understood, and agree to all the terms and conditions outlined
                in this proposal. This action is{" "}
                <strong>legally binding</strong> and{" "}
                <strong>cannot be reversed</strong>.
              </Text>
            </VStack>
          </HStack>
        </Box>
      </Box>

      {/* Confirmation Modal */}
      <AlertDialog
        isOpen={isDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDialogOpen(false)}
        isCentered
        motionPreset="slideInBottom"
      >
        <AlertDialogOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
        <AlertDialogContent
          borderRadius="2xl"
          mx={4}
          boxShadow="2xl"
          border="1px solid"
          borderColor="green.100"
        >
          <AlertDialogHeader
            fontSize="lg"
            fontWeight="900"
            borderBottom="1px solid"
            borderColor="gray.100"
            pb={4}
          >
            <Flex align="center" gap={3}>
              <Box
                bgGradient="linear(to-br, green.400, green.600)"
                p={2}
                borderRadius="xl"
              >
                <Icon as={CheckCircle} w={5} h={5} color="white" />
              </Box>
              <Text>Confirm Your Signature</Text>
            </Flex>
          </AlertDialogHeader>

          <AlertDialogBody py={6}>
            <VStack align="stretch" spacing={4}>
              <Box
                bg="orange.50"
                border="1px solid"
                borderColor="orange.200"
                borderRadius="lg"
                p={4}
              >
                <HStack spacing={2} mb={2}>
                  <Icon as={AlertTriangle} w={4} h={4} color="orange.600" />
                  <Text fontSize="sm" fontWeight="700" color="orange.900">
                    This action cannot be undone
                  </Text>
                </HStack>
                <Text fontSize="xs" color="orange.800" lineHeight="1.6">
                  Once you proceed, your signature will be permanently recorded
                  and this proposal will be marked as <strong>ACCEPTED</strong>.
                </Text>
              </Box>

              <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                Are you sure you want to proceed with accepting this proposal?
              </Text>
            </VStack>
          </AlertDialogBody>

          <AlertDialogFooter
            borderTop="1px solid"
            borderColor="gray.100"
            pt={4}
          >
            <Button
              ref={cancelRef}
              onClick={() => setIsDialogOpen(false)}
              size="md"
              variant="ghost"
              mr={3}
            >
              Cancel
            </Button>
            <Button
              leftIcon={<FaCheck />}
              onClick={handleConfirm}
              size="md"
              bgGradient="linear(to-r, green.400, green.600)"
              color="white"
              _hover={{
                bgGradient: "linear(to-r, green.500, green.700)",
              }}
              fontWeight="700"
            >
              Yes, Accept Proposal
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default ESignCanvas;
